import {cn} from '@/lib/utils'

/** Inventario cerrado (U18): un glifo por concepto, en rejilla 16×16, `aria-hidden` (el nombre lo pone el
 * control, U17) y `currentColor` (el tono lo pone el bloque, U2). */
type GlyphProps = {
  size?: number
  className?: string
}

/** «Ayuda de esta pantalla» (U6, U16), distinto del «i» de `InfoGlyph` («qué es este dato»). */
export function QuestionGlyph({size = 16, className}: GlyphProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" className={className}>
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.3" />
      <path
        d="M6.3 6.2c0-1 .8-1.7 1.9-1.7s1.8.6 1.8 1.5c0 .8-.5 1.1-1 1.5-.5.4-.8.6-.8 1.2"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
      <circle cx="8.2" cy="11.1" r="0.15" fill="currentColor" stroke="currentColor" strokeWidth="0.9" />
    </svg>
  )
}

export function InfoGlyph({size = 14, className}: GlyphProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" className={className}>
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.3" />
      <path d="M8 7.2v3.6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <circle cx="8" cy="5" r="0.15" fill="currentColor" stroke="currentColor" strokeWidth="0.9" />
    </svg>
  )
}

/** Apunta hacia abajo; la rotación la pone quien lo usa. */
export function ChevronGlyph({size = 11, className}: GlyphProps) {
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn('shrink-0 motion-safe:transition-transform motion-safe:duration-200', className)}>
      <path d="M4 6l4 4 4-4" />
    </svg>
  )
}

export function CheckGlyph({size = 14, className}: GlyphProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" className={className}>
      <path
        d="M3.5 8.5 6.5 11.5 12.5 4.5"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Solo la casilla indeterminada de `ui/checkbox.tsx`; «quitar» sería otro concepto y otro glifo. */
export function MinusGlyph({size = 13, className}: GlyphProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" className={className}>
      <path d="M4 8h8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  )
}

/** «Falta un paso» o acceso restringido, nunca junto a una confirmación (esa va con `CheckGlyph`). El
 * motivo siempre en `aria-label` o `sr-only` (U17). */
export function LockGlyph({size = 12, className}: GlyphProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" className={className}>
      <rect x="3.5" y="7" width="9" height="6.5" rx="1.4" stroke="currentColor" strokeWidth="1.3" />
      <path d="M5.5 7V5.2a2.5 2.5 0 0 1 5 0V7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  )
}

export function TrashGlyph({size = 14, className}: GlyphProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" className={className}>
      <path
        d="M2.5 4.5h11M6 4.5V3h4v1.5M4.2 4.5l.6 8.5h6.4l.6-8.5M6.7 7v4M9.3 7v4"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function PencilGlyph({size = 14, className}: GlyphProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" className={className}>
      <path
        d="m11.3 2.7 2 2L5.5 12.5 2.8 13.2l.7-2.7 7.8-7.8Z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Agregar un renglón a una lista a la vista (KAI-334); el primario de una vista es un botón con texto. */
export function PlusGlyph({size = 14, className}: GlyphProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" className={className}>
      <path d="M8 3.5v9M3.5 8h9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

export function NotesGlyph({size = 14, className}: GlyphProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" className={className}>
      <path d="M3 3.5h10v6.5l-3 3H3v-9.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M5.5 6.5h5M5.5 8.7h3" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
    </svg>
  )
}

export function SearchGlyph({size = 13, className}: GlyphProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" className={className}>
      <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="m11 11 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

export function MenuGlyph({size = 18, className}: GlyphProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" className={className}>
      <path d="M2 4h12M2 8h12M2 12h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

export function CloseGlyph({size = 14, className}: GlyphProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" className={className}>
      <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

export function ArrowLeftGlyph({size = 14, className}: GlyphProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}>
      <path d="M6.5 3 2.5 8l4 5" />
      <path d="M2.5 8h11" />
    </svg>
  )
}

/** Solo lo que abre otra pestaña; navegar dentro de la app lleva `ChevronGlyph` rotado (U18(a)). */
export function ExternalLinkGlyph({size = 14, className}: GlyphProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}>
      <path d="M3.7 12.3 12.3 3.7" />
      <path d="M5.6 3.7h6.7v6.7" />
    </svg>
  )
}

export function DownloadGlyph({size = 13, className}: GlyphProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" className={className}>
      <path
        d="M8 2.5v8m0 0 3-3m-3 3-3-3M3 12.5h10"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function BellGlyph({size = 16, className}: GlyphProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" className={className}>
      <path
        d="M8 1.75a3.75 3.75 0 0 0-3.75 3.75c0 2.2-.53 3.4-1.02 4.06-.28.38-.01.94.46.94h8.62c.47 0 .74-.56.46-.94-.49-.66-1.02-1.86-1.02-4.06A3.75 3.75 0 0 0 8 1.75ZM6.5 12.5a1.5 1.5 0 0 0 3 0"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function KebabGlyph({size = 16, className}: GlyphProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" className={className}>
      <circle cx="3.5" cy="8" r="1.3" fill="currentColor" />
      <circle cx="8" cy="8" r="1.3" fill="currentColor" />
      <circle cx="12.5" cy="8" r="1.3" fill="currentColor" />
    </svg>
  )
}

export function PersonGlyph({size = 14, className}: GlyphProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" className={className}>
      <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm0 1.5c-2.9 0-5.5 1.5-5.5 3.4V14h11v-1.1c0-1.9-2.6-3.4-5.5-3.4Z" />
    </svg>
  )
}

/** «Reportar algo que pasó», no una advertencia: un estado que exige atención va con el tono de `ui/tag.tsx` (U2). */
export function AlertTriangleGlyph({size = 16, className}: GlyphProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}>
      <path d="M8 2 1.3 13.5h13.4L8 2Z" />
      <path d="M8 6.5v3" />
      <path d="M8 11.2h.01" />
    </svg>
  )
}

/** Feedback del topbar (docs/modulos/feedback-app-spec.md), distinto del «?» y del «i». */
export function CommentGlyph({size = 16, className}: GlyphProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" className={className}>
      <path
        d="M2.5 3.8A1.8 1.8 0 0 1 4.3 2h7.4a1.8 1.8 0 0 1 1.8 1.8v5.4a1.8 1.8 0 0 1-1.8 1.8H7.2l-3.1 2.6v-2.6h-.8a1.8 1.8 0 0 1-1.8-1.8V3.8Z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
      <path d="M5.4 5.6h5.2M5.4 7.9h3.4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  )
}
